/**
 * DTOs fuer das Onboarding-Q&A-Modul (Plan-MD §19.3).
 * Backend liefert PascalCase, Frontend nimmt beides an.
 */

/**
 * Antwort-Art einer Assistant-Bubble. Erweiterungs-Seam: heute nur `answer`
 * (Prosa) und `guide` (Antwort mit hinterlegter Tour) aktiv. `command` ist
 * reserviert fuer den spaeteren Assistenten (Befehle ausfuehren) — siehe
 * docs/design-proposals/help-onboarding-redesign-2026-05-27/help-chat-commands.html.
 */
export type HelpResponseType = 'answer' | 'guide' | 'command';

export interface ConversationTurn {
  role: 'user' | 'assistant';
  content: string;
  sources?: HelpSource[];
  related?: HelpRelated[];
  feedbackId?: string;
  responseType?: HelpResponseType;
  /** Schreib-Vorschlag (Command-Chatbot): zeigt eine Bestaetigen/Abbrechen-Card. */
  proposal?: ChatbotProposal;
  /** Sammel-Bestaetigung: mehrere Aktionen aus EINER Nachricht (eine Card mit Liste). */
  proposalBatch?: ChatbotProposalBatch;
  /** true, sobald der Vorschlag bestaetigt/abgelehnt wurde (Card ausblenden). */
  proposalHandled?: boolean;
  /**
   * Name des Werkzeugs, das diese Antwort AUSGEFUEHRT hat — gesetzt nur, wenn der Nutzer
   * gerade eine Schreibaktion BESTAETIGT hat (B2, Ergebniskarte).
   *
   * Ohne dieses Feld lässt sich „Antwort auf eine Frage" nicht von „Ergebnis einer
   * Schreibaktion" unterscheiden: beides ist ein Assistant-Turn mit Text. Ein „Im Plan
   * ansehen" unter jeder Antwort wäre Rauschen; unter dem Ergebnis ist es der Weg zurück
   * in die Arbeit.
   */
  executedTool?: string;
  /** Strukturierte Darstellung (native Karten) statt Text-Bubble, falls vom Backend geliefert. */
  blocks?: ChatBlock[];
  /**
   * Die Hilfe hatte keinen Treffer (Review e, 02.11.2026): Unter der Antwort stehen dann die
   * Auswege aus `HELP_CHAT.NO_MATCH_HINTS` (anders fragen, Tour der Seite, Support). Der
   * Schlüssel stand seit Mai in allen Sprachdateien, aber keine Fläche las ihn.
   */
  noMatch?: boolean;
}

/** Schreib-Vorschlag des Command-Chatbots (erst nach Confirm ausgefuehrt). */
export interface ChatbotProposal {
  executionId: string;
  toolName: string;
  summary: string;
  resolvedArguments: Record<string, unknown>;

  // ── Trockenlauf (Vertragspunkt 3, ADDITIV) ────────────────────────────────
  // Alles hier ist optional und ergaenzt `resolvedArguments`, ersetzt es nicht.
  // Solange das Backend nichts liefert, faellt die Vorschau-Karte auf das
  // Anzeigemodell ueber `resolvedArguments` zurueck (proposal-preview.ts).

  /**
   * Bereits ANZEIGEFERTIGE Felder (Label + Wert, uebersetzt und formatiert).
   *
   * ⚠️ Kommen hier rohe Feldnamen an (`strategyId`, `fromUtc`), ist das ein Backend-Fehler
   * und kein Anlass, sie zu zeigen — {@link isDisplayReadyPreview} sortiert solche
   * Lieferungen aus und die Karte nimmt das eigene Anzeigemodell.
   */
  preview?: ChatField[];
  /**
   * Zahl der betroffenen Objekte aus einem echten Trockenlauf.
   *
   * ⚠️ `null`/fehlend heisst „kein Trockenlauf" — dann verschwindet das Feld „Betroffen"
   * ganz. Es wird NICHT geschaetzt: eine geratene Zahl vor einer Schreibaktion ist
   * schlimmer als gar keine.
   */
  affectedCount?: number;
  /** Einheit zu {@link affectedCount}, z. B. „offene Schichten". */
  affectedLabel?: string;
  /** Zustand nach dem Ausfuehren, z. B. „Entwurf – nicht veroeffentlicht". */
  resultState?: string;
}

/** Sammel-Bestaetigung: mehrere Aktionen, EINE ExecutionId (BatchId) fuer den Confirm. */
export interface ChatbotProposalBatch {
  executionId: string;
  summary: string;
  items: { toolName: string; summary: string }[];
}

export interface ChatbotSendRequest {
  message: string;
  /**
   * Kanonischer Sprach-Short-Code aus dem `LanguageService` (de|en|fr|pl|ro|tr|ar).
   *
   * ⚠️ **Das Feld heisst `language`, nicht `locale`.** Bis zum 10.09.2026 sendete der
   * Client `locale` — das DTO auf der Gegenseite heisst `ChatbotSendDto.Language`
   * (`Application/CommandChatbot/Models/ChatbotModels.cs`), und ein unbekanntes Feld
   * verwirft das Model-Binding stillschweigend. E1 war damit nur zur Hälfte eingelöst:
   * Der Aufrufer holte die Sprache korrekt aus dem `LanguageService` (statt des früheren
   * `currentLang === 'de' ? 'de' : 'de'`) und legte sie in ein Feld, das niemand liest.
   * `request.Language` kam als `null` an, und `ChatbotPrompts.LanguageDirective(null)`
   * heisst „deutsch wie bisher" — ein englischsprachiger Nutzer bekam weiterhin deutsche
   * Antworten. Gefunden beim Vergleich der echten Nutzlast (Netzwerk-Mitschnitt) mit dem
   * Backend-DTO; kein Test und kein Wächter sieht einen Feldnamen auf dem Draht.
   */
  language?: string;
  conversationId?: string;
  context?: { role: string; content: string }[];
}

export interface ChatbotConfirmRequest {
  executionId: string;
  confirmed: boolean;
  conversationId?: string;
}

/**
 * Strukturierte Antwort-Darstellung (native Karten) — vom Backend bei Top-Archetypen geliefert.
 *
 * `delta` (Zeitvergleich) und `choice` (Mehrdeutigkeit) sind die zwei Typen aus den
 * Vertragspunkten 1 und 2. Das Frontend rendert sie, bevor das Backend sie liefert;
 * ein UNBEKANNTER Typ faellt still auf den Textpfad zurueck — das leistet der Filter in
 * `normalizeBlocks`, nicht das Template: Ein Block ohne Zweig wuerde dort eine leere
 * Nachricht erzeugen, weil die Bubble Text ODER Bloecke zeigt, nie beides.
 */
export type ChatBlockType =
  | 'section'
  | 'kpi'
  | 'ranking'
  | 'coverage'
  | 'fields'
  | 'delta'
  | 'choice';

export interface ChatBlock {
  type: ChatBlockType;
  title?: string;
  count?: string;
  note?: string;
  footer?: string;
  items?: ChatListItem[];
  tiles?: ChatKpi[];
  rows?: ChatRankRow[];
  coverage?: ChatCoverageRow[];
  fields?: ChatField[];
  /** Vertragspunkt 1 — Zeitvergleich (`type: 'delta'`). */
  deltas?: ChatDeltaRow[];
  /** Vertragspunkt 2 — Auswahl bei Mehrdeutigkeit (`type: 'choice'`). */
  options?: ChatChoiceOption[];
}

/** Eine Zeile im Zeitvergleich: zwei Werte, ihre Differenz und deren Richtung. */
export interface ChatDeltaRow {
  label: string;
  current: string;
  previous: string;
  /** Bereits formatierte Differenz, z. B. „+12 %" oder „−3 Std.". */
  deltaText: string;
  /**
   * Richtung der Veraenderung. Bewusst NICHT als Wertung: ob „mehr" gut oder schlecht
   * ist, haengt an der Kennzahl (mehr Umsatz vs. mehr Überstunden) — dafuer gibt es
   * {@link critical}.
   */
  direction: 'up' | 'down' | 'flat';
  /** Hebt die Zeile als beachtenswert hervor (⚠), unabhaengig von der Richtung. */
  critical?: boolean;
}

/**
 * Eine antippbare Option bei einer Rueckfrage („Welche Kerstin meinst du?").
 * `meta` traegt das Unterscheidungsmerkmal — bei Personen die Abteilung.
 */
export interface ChatChoiceOption {
  title: string;
  meta?: string;
  /** Heute nur `'disambiguate'`; offen fuer weitere Rueckfrage-Arten. */
  action?: string;
  actionArg?: string;
}

export interface ChatListItem {
  title: string;
  meta?: string;
  badge?: string;
  /** date | urgent | high | warn | neutral — steuert die (blaue/neutrale) Chip-Variante. */
  badgeKind?: string;
  warn?: string;
  /** Folgeaktion bei Klick: 'profile' | 'open_shift'. Leer = nicht klickbar. */
  action?: string;
  actionArg?: string;
}

export interface ChatKpi {
  label: string;
  value: string;
  detail?: string;
  emphasis?: boolean;
}

export interface ChatRankRow {
  rank: number;
  name: string;
  value: string;
  /** 0..1 fuer den Balken. */
  fraction?: number;
}

export interface ChatCoverageRow {
  label: string;
  /** -1 = nicht anwendbar (geschlossen / kein Bedarf) → FE zeigt keinen Balken. */
  percent: number;
  detail?: string;
  critical?: boolean;
}

export interface ChatField {
  label: string;
  value: string;
}

export interface ChatbotResponse {
  reply: string;
  proposal?: ChatbotProposal;
  proposalBatch?: ChatbotProposalBatch;
  results?: any;
  blocks?: ChatBlock[];
  conversationId?: string;
  traceId?: string;
  /** Kein Hilfe-Treffer — siehe {@link ConversationTurn.noMatch}. */
  noMatch?: boolean;
}

/**
 * Rest-Stand des KI-Kontingents einer Self-Service-Demo-Session (Backend GET /Chatbot/DemoAiQuota).
 * `isDemo=false` fuer echte Firmen → das Frontend blendet Badge/Hinweis dann aus.
 */
export interface DemoAiQuota {
  isDemo: boolean;
  used: number;
  quota: number;
  remaining: number;
}

/** Normalisiert die Demo-KI-Kontingent-Antwort (PascalCase ODER camelCase). */
export function normalizeDemoAiQuota(raw: any): DemoAiQuota | null {
  if (!raw) return null;
  const data = raw.Data ?? raw.data ?? raw;
  return {
    isDemo: (data.IsDemo ?? data.isDemo ?? false) === true,
    used: Number(data.Used ?? data.used ?? 0),
    quota: Number(data.Quota ?? data.quota ?? 0),
    remaining: Number(data.Remaining ?? data.remaining ?? 0),
  };
}

// ⚠️ `AskHelpRequest` stand hier bis zum 10.09.2026 und hatte keinen einzigen Verwender —
// der Sende-Vertrag ist `ChatbotSendRequest` oben. Die Datei führte damit ZWEI
// Anfrage-Formen für einen Endpunkt, und die tote trug ausgerechnet den falschen Feldnamen
// (`locale`) weiter, der den echten Fehler mitverursacht hat.

export interface AskHelpResponse {
  answer: string;
  sources: HelpSource[];
  related: HelpRelated[];
  feedbackId: string;
  fromCache: boolean;
  responseType?: HelpResponseType;
}

/** Kuratierter "Siehe auch"-Querverweis (Prozesskette) — aus RelatedSlugs aufgeloest. */
export interface HelpRelated {
  slug: string;
  title: string;
}

export interface HelpSource {
  slug: string;
  title: string;
  tourKey?: string;
  /** Feature-Key (z.B. "SHIFT_PLANNING_BASIC"), den diese Anleitung voraussetzt. Treibt den Settings-Preflight. */
  requiresFeature?: string;
  distance: number;
  matchedSource: 'Title' | 'AlternativeTitle' | 'Body';
}

export interface SubmitHelpFeedback {
  feedbackId: string;
  helpful: boolean;
  feedbackText?: string;
}

/** Normalisiert die Command-Chatbot-Antwort (PascalCase ODER camelCase). */
export function normalizeChatbotResponse(raw: any): ChatbotResponse | null {
  if (!raw) return null;
  const data = raw.Data ?? raw.data ?? raw;
  const rawProposal = data.Proposal ?? data.proposal ?? null;
  const proposal: ChatbotProposal | undefined = rawProposal
    ? {
        executionId: rawProposal.ExecutionId ?? rawProposal.executionId ?? '',
        toolName: rawProposal.ToolName ?? rawProposal.toolName ?? '',
        summary: rawProposal.Summary ?? rawProposal.summary ?? '',
        resolvedArguments: rawProposal.ResolvedArguments ?? rawProposal.resolvedArguments ?? {},
        // Vertragspunkt 3 — Trockenlauf, additiv. `affectedCount` bleibt bewusst
        // `undefined`, wenn das Backend nichts schickt: die Karte laesst das Feld dann
        // WEG, statt eine Null oder eine Schaetzung zu zeigen.
        preview: mapArr(
          rawProposal.Preview ?? rawProposal.preview,
          (f: any): ChatField => ({
            label: pick(f, 'Label', 'label') ?? '',
            value: String(pick(f, 'Value', 'value') ?? ''),
          }),
        ),
        affectedCount: numOrUndefined(
          rawProposal.AffectedCount ?? rawProposal.affectedCount,
        ),
        affectedLabel: rawProposal.AffectedLabel ?? rawProposal.affectedLabel ?? undefined,
        resultState: rawProposal.ResultState ?? rawProposal.resultState ?? undefined,
      }
    : undefined;
  const rawBatch = data.ProposalBatch ?? data.proposalBatch ?? null;
  const rawItems = (rawBatch?.Items ?? rawBatch?.items ?? []) as any[];
  const proposalBatch: ChatbotProposalBatch | undefined = rawBatch
    ? {
        executionId: rawBatch.ExecutionId ?? rawBatch.executionId ?? '',
        summary: rawBatch.Summary ?? rawBatch.summary ?? '',
        items: rawItems.map((i) => ({
          toolName: i.ToolName ?? i.toolName ?? '',
          summary: i.Summary ?? i.summary ?? '',
        })),
      }
    : undefined;
  return {
    reply: data.Reply ?? data.reply ?? '',
    proposal,
    proposalBatch,
    results: data.Results ?? data.results ?? undefined,
    blocks: normalizeBlocks(data.Blocks ?? data.blocks),
    conversationId: data.ConversationId ?? data.conversationId ?? undefined,
    traceId: data.TraceId ?? data.traceId ?? undefined,
    noMatch: pick(data, 'NoMatch', 'noMatch') === true || isHelpNoMatchReply(data.Reply ?? data.reply),
  };
}

/**
 * Feste Absage des Servers, wenn die Hilfe nichts findet
 * (`AnswerHelpQuestionTool.cs`, Zweig `noMatch`).
 *
 * ⚠️ Übergang: `ChatbotResponseDto` trägt das Signal (`ChatbotToolResult.NoMatch`) heute
 * NICHT bis zum Client — es endet in der Telemetrie. Bis ein Feld `NoMatch` mitreist (wird
 * oben schon gelesen), bleibt nur der Satzanfang. Er ist deutsch, weil der Server ihn in
 * jeder Sprache deutsch schickt; ändert der Server den Satz, fallen die Auswege still weg.
 */
export const HELP_NO_MATCH_REPLY_PREFIX = 'Dazu habe ich keine passende Anleitung gefunden';

export function isHelpNoMatchReply(reply: unknown): boolean {
  return typeof reply === 'string' && reply.trimStart().startsWith(HELP_NO_MATCH_REPLY_PREFIX);
}

/** Liest einen Wert tolerant unter PascalCase ODER camelCase. */
function pick<T = any>(o: any, ...keys: string[]): T | undefined {
  for (const k of keys) {
    if (o?.[k] !== undefined && o?.[k] !== null) return o[k] as T;
  }
  return undefined;
}

function mapArr<T>(arr: any, fn: (x: any) => T): T[] | undefined {
  return Array.isArray(arr) && arr.length > 0 ? arr.map(fn) : undefined;
}

/**
 * Liest eine Zahl — oder `undefined`, wenn keine da ist.
 *
 * ⚠️ Nicht `Number(x) || 0`: Das machte aus „kein Trockenlauf" ein „0 betroffene
 * Objekte", und genau diese Zeile ist der Unterschied zwischen einem weggelassenen
 * Feld und einer falschen Zusage vor einer Schreibaktion.
 */
function numOrUndefined(raw: unknown): number | undefined {
  if (raw === null || raw === undefined || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Blocktypen, fuer die `chat-blocks` einen Zweig hat.
 *
 * Die Liste ist kein Duplikat von `ChatBlockType`, sondern deren Laufzeit-Gegenstueck: Ein
 * Typ, den das Backend spaeter erfindet, ist zur Bauzeit unbekannt und muss zur Laufzeit
 * aussortiert werden. Wer hier einen Typ eintraegt, ohne ihn in `chat-blocks` zu rendern,
 * baut genau den Fehler wieder ein, den diese Liste verhindert.
 */
const RENDERBARE_BLOCKTYPEN: ReadonlySet<string> = new Set<ChatBlockType>([
  'section',
  'kpi',
  'ranking',
  'coverage',
  'fields',
  'delta',
  'choice',
]);

/**
 * Normalisiert die strukturierten Bloecke (Backend liefert PascalCase, Tests evtl. camelCase).
 *
 * Unbekannte Typen werden **verworfen**, und bleibt nichts uebrig, gibt die Funktion
 * `undefined` zurueck. Das ist keine Kosmetik: Die Bubble zeigt Text ODER Bloecke
 * (`help-chat.component.html:96`), nie beides — ein Block ohne Zweig erzeugt sonst eine
 * **leere Nachricht** statt des Antwortsatzes, der im `Reply` bereits dasteht.
 */
export function normalizeBlocks(raw: any): ChatBlock[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const blocks = raw.map(
    (b: any): ChatBlock => ({
      type: (pick<string>(b, 'Type', 'type') ?? 'section') as ChatBlockType,
      title: pick(b, 'Title', 'title'),
      count: pick(b, 'Count', 'count'),
      note: pick(b, 'Note', 'note'),
      footer: pick(b, 'Footer', 'footer'),
      items: mapArr(pick(b, 'Items', 'items'), (i: any) => ({
        title: pick(i, 'Title', 'title') ?? '',
        meta: pick(i, 'Meta', 'meta'),
        badge: pick(i, 'Badge', 'badge'),
        badgeKind: pick(i, 'BadgeKind', 'badgeKind'),
        warn: pick(i, 'Warn', 'warn'),
        action: pick(i, 'Action', 'action'),
        actionArg: pick(i, 'ActionArg', 'actionArg'),
      })),
      tiles: mapArr(pick(b, 'Tiles', 'tiles'), (t: any) => ({
        label: pick(t, 'Label', 'label') ?? '',
        value: String(pick(t, 'Value', 'value') ?? ''),
        detail: pick(t, 'Detail', 'detail'),
        emphasis: !!pick(t, 'Emphasis', 'emphasis'),
      })),
      rows: mapArr(pick(b, 'Rows', 'rows'), (r: any) => ({
        rank: Number(pick(r, 'Rank', 'rank') ?? 0),
        name: pick(r, 'Name', 'name') ?? '',
        value: String(pick(r, 'Value', 'value') ?? ''),
        fraction: pick(r, 'Fraction', 'fraction') != null ? Number(pick(r, 'Fraction', 'fraction')) : undefined,
      })),
      coverage: mapArr(pick(b, 'Coverage', 'coverage'), (c: any) => ({
        label: pick(c, 'Label', 'label') ?? '',
        percent: Number(pick(c, 'Percent', 'percent') ?? -1),
        detail: pick(c, 'Detail', 'detail'),
        critical: !!pick(c, 'Critical', 'critical'),
      })),
      fields: mapArr(pick(b, 'Fields', 'fields'), (f: any) => ({
        label: pick(f, 'Label', 'label') ?? '',
        value: String(pick(f, 'Value', 'value') ?? ''),
      })),
      // Vertragspunkt 1 — Zeitvergleich.
      deltas: mapArr(pick(b, 'Deltas', 'deltas'), (d: any) => ({
        label: pick(d, 'Label', 'label') ?? '',
        current: String(pick(d, 'Current', 'current') ?? ''),
        previous: String(pick(d, 'Previous', 'previous') ?? ''),
        deltaText: String(pick(d, 'DeltaText', 'deltaText') ?? ''),
        direction: normalizeDeltaDirection(pick(d, 'Direction', 'direction')),
        critical: !!pick(d, 'Critical', 'critical'),
      })),
      // Vertragspunkt 2 — Auswahl bei Mehrdeutigkeit.
      options: mapArr(pick(b, 'Options', 'options'), (o: any) => ({
        title: pick(o, 'Title', 'title') ?? '',
        meta: pick(o, 'Meta', 'meta'),
        action: pick(o, 'Action', 'action'),
        actionArg: pick(o, 'ActionArg', 'actionArg'),
      })),
    }),
  );

  const renderbar = blocks.filter((b) => RENDERBARE_BLOCKTYPEN.has(b.type));
  return renderbar.length ? entdoppelteRueckfrage(renderbar) : undefined;
}

/**
 * Entfernt die leere Titel-Karte VOR einer Auswahlkarte mit demselben Titel.
 *
 * ⚠️ **Am Bildschirm gefunden, nicht im Code.** `ChatbotDeadEnd.Ambiguous` baut die
 * Rückfrage als ZWEI Blöcke: einen Aussage-Block mit der Frage („Wen meinst du mit 'Sch'?")
 * und danach den `choice`-Block, der dieselbe Frage als Kartenkopf trägt. Beide gerendert
 * ergibt eine leere Karte, die nur eine Überschrift enthält, und unmittelbar darunter
 * dieselbe Überschrift ein zweites Mal — gemessen am Gerät (390×844, 10.09.2026): 68 px
 * für null Information.
 *
 * Die Regel ist bewusst ENG: Sie greift nur, wenn der Aussage-Block gar keine Zeilen hat
 * UND sein Titel Zeichen für Zeichen dem der folgenden Auswahlkarte entspricht. Ein
 * Aussage-Block ohne Zeilen ist der Normalfall jeder Antwort („Nein — 9 Ruhezeit-Verstöße")
 * und muss bleiben; nur die Dopplung fliegt.
 *
 * Hier und nicht im Template, weil das Template drei Stellen prüfen müsste (Titel, Zeilen,
 * Nachbarschaft) — und weil die Reihenfolge eine Eigenschaft der Antwort ist, keine der
 * Darstellung.
 */
function entdoppelteRueckfrage(blocks: ChatBlock[]): ChatBlock[] {
  return blocks.filter((b, i) => {
    if (b.type !== 'section' || (b.items?.length ?? 0) > 0 || !b.title) return true;
    const naechster = blocks[i + 1];
    return !(naechster?.type === 'choice' && naechster.title === b.title);
  });
}

/**
 * Haertet die Richtung eines Delta-Werts ab.
 *
 * Ein unbekannter oder fehlender Wert wird `'flat'` und nicht etwa geraten: Eine falsche
 * Pfeilrichtung behauptet eine Aussage, die die Daten nicht hergeben.
 */
function normalizeDeltaDirection(raw: unknown): 'up' | 'down' | 'flat' {
  const v = String(raw ?? '').toLowerCase();
  return v === 'up' || v === 'down' ? v : 'flat';
}

/**
 * Vertragspunkt 4 — eine vom Backend im Transkript erkannte Entitaet.
 * `start`/`length` sind Zeichenpositionen im gelieferten `text`.
 */
export interface TranscriptEntity {
  start: number;
  length: number;
  kind: 'employee' | 'department';
  displayName: string;
  id?: string | number;
}

/** Antwort von `POST /Chatbot/Transcribe`: Text plus optional aufgeloeste Entitaeten. */
export interface TranscriptResult {
  text: string;
  entities: TranscriptEntity[];
}

/**
 * Normalisiert die Transkript-Antwort (PascalCase ODER camelCase).
 *
 * `entities` ist heute leer — das Backend loest Namen erst IM Werkzeug auf, also nach dem
 * Senden (Vertragspunkt 4 ist die Zusage, das vorzuziehen). Bis dahin markiert das
 * Frontend selbst gegen `employeeNameList`; siehe `markTranscriptEntities()`.
 */
export function normalizeTranscript(raw: any): TranscriptResult {
  const data = raw?.Data ?? raw?.data ?? raw;
  const rawEntities = (pick<any[]>(data, 'Entities', 'entities') ?? []) as any[];
  return {
    text: String(pick(data, 'Text', 'text') ?? ''),
    entities: rawEntities
      .map((e: any) => ({
        start: Number(pick(e, 'Start', 'start') ?? -1),
        length: Number(pick(e, 'Length', 'length') ?? 0),
        kind: (String(pick(e, 'Kind', 'kind') ?? 'employee').toLowerCase() === 'department'
          ? 'department'
          : 'employee') as 'employee' | 'department',
        displayName: String(pick(e, 'DisplayName', 'displayName') ?? ''),
        id: pick(e, 'Id', 'id'),
      }))
      // Eine Markierung ausserhalb des Textes wäre ein sichtbarer Fehler statt einer Hilfe.
      .filter((e) => e.start >= 0 && e.length > 0),
  };
}

/** Hilfs-Typ: Backend kann sowohl camelCase als auch PascalCase liefern. */
export function normalizeHelpResponse(raw: any): AskHelpResponse | null {
  if (!raw) return null;
  const data = raw.Data ?? raw.data ?? raw;
  const sources = (data.Sources ?? data.sources ?? []) as any[];
  const related = (data.Related ?? data.related ?? []) as any[];
  return {
    answer: data.Answer ?? data.answer ?? '',
    feedbackId: data.FeedbackId ?? data.feedbackId ?? '',
    fromCache: data.FromCache ?? data.fromCache ?? false,
    responseType: (data.ResponseType ?? data.responseType ?? undefined) as HelpResponseType | undefined,
    related: related.map((r) => ({
      slug: r.Slug ?? r.slug ?? '',
      title: r.Title ?? r.title ?? '',
    })),
    sources: sources.map((s) => ({
      slug: s.Slug ?? s.slug ?? '',
      title: s.Title ?? s.title ?? '',
      tourKey: s.TourKey ?? s.tourKey ?? undefined,
      requiresFeature: s.RequiresFeature ?? s.requiresFeature ?? undefined,
      distance: s.Distance ?? s.distance ?? 0,
      matchedSource: (s.MatchedSource ?? s.matchedSource ?? 'Title') as
        | 'Title'
        | 'AlternativeTitle'
        | 'Body',
    })),
  };
}
